/* eslint-disable react/prop-types */
import {
  Accordion,
  AccordionButton, AccordionIcon,
  AccordionItem, AccordionPanel,
  Box,
  Flex,
  Grid,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Select,
  Text,
  Textarea,
  useColorModeValue,
  useToast, Switch, Checkbox, VStack,
} from '@chakra-ui/react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { ArrowDownIcon, ArrowUpIcon } from '@chakra-ui/icons';
import React, { useMemo, useRef, useState } from 'react';
import { NIL, v4 } from 'uuid';
import {
  FiBookmark,
  FiFileText,
  FiCopy,
  FiMoreVertical,
} from 'react-icons/fi';
import { clearBackorder, setToFollow, startBackorder } from '../utils/backorder'; // item 1577
import {
  Item, getEmptyItem, PoPriceTier, PoTierCosts, poTierCosts, poTierCost, resolvePoTier,
} from '../models/item';
import { DEFAULT_PRICING_SETTINGS } from '../models/pricingSettings';
import useItem from '../hooks/useItem';
import useItems from '../hooks/useItems';
import { DocumentItemLike } from './forms/DocumentItemForm';
import CatalogCombobox from './forms/CatalogCombobox';
import { money } from '../utils/money'; // item 459 — stealth-aware live totals

// The shared item-entry surface for all five document types (item 79 rebuild):
// items are grouped by category, and each line is an INLINE-EDITABLE row — every
// field (sku / brand / cost / retail / qty) edits in place, no modal. A search
// combobox per category drops a prefilled row from the catalog or a blank row for
// a brand-new line. Per-type wrappers choose the money columns + the catalog
// converters, exactly as before.

export interface DocumentLineItemLike extends DocumentItemLike {
  id: string;
  category: string;
  description?: string;
  notes?: string; // per-line free-text note (item 80)
  employeeId?: string | null; // item 943 — per-line "Performed by" (invoices)
}

export interface DocumentItemsFormElementSharedProps<TItem> {
  categoryNames: string;
  items: TItem[];
  setItems: (newItems: TItem[]) => void;
  categoryButtonHandler: (cmd: string, name: string, e: any) => void;
  activeCategory: string;
  categoryEditMode: boolean;
  editCategoryName: string;
  setEditCategoryName: (name: string) => void;
  onAddClose: () => void;
  isAddOpen: boolean;
}

interface DocumentItemsFormElementProps<TItem extends DocumentLineItemLike>
  extends DocumentItemsFormElementSharedProps<TItem> {
  showCost?: boolean;
  showRetail?: boolean;
  // item 434 — the MSRP MARKUP ⇄ MSRP pair (rides ListPrice; invoices have no
  // per-line ListPrice column, so they don't opt in)
  showMsrpPair?: boolean;
  // item 556 — the multiplier column is never blank: when a line has no MSRP yet it
  // shows this company-default markup as the starting point (per-doc-type: SRP on
  // proposals/invoices/POs, MSRP on sales orders — the wrapper passes its own).
  defaultMsrpMult?: number;
  // canonical model (2026-07-14) — the DISTRO→GRP markup ⇄ GROUP PRICE pair for HARDWARE
  // lines (mirrors the MSRP pair one tier down: base = cost/DISTRO, target = GROUP PRICE).
  // Only meaningful when BOTH cost + retail show; per-line it's gated on isHardware.
  showGrpPair?: boolean;
  defaultGrpMult?: number; // company default group markup, seeds the GRP ×MULT cell
  showPriceTier?: boolean; // item 290 — per-line Dealer/Distributor cost-tier selector (POs)
  // item 554 — on EXPENSE docs (PO/Bill) every line is money you actually pay, service or
  // hardware, so COST is always an editable field. The item-425 "cost is hardware-only, dash
  // for services" rule is a REVENUE-side (proposal/invoice) concept only. This is also what
  // lets a PO carry a LABOR line at cost (labor ignores the distributor tier).
  costAlwaysEditable?: boolean;
  // item 554 — relabel the COST column (POs call their buy price "DISTO" — distributor).
  costLabel?: string;
  // item 796 (Chad) — relabel the retail TOTAL (a PO's "total" IS the group price).
  totalLabel?: string;
  vendorsOnly?: boolean;
  // item 1234 (Chad) — WO/SO only: a one-shot "% off GROUP pricing" control by the totals.
  // Applying rewrites every line's retailEach (the binding group price) — services AND
  // hardware — never cost, never MSRP/ListPrice. Draft-state edit; save persists it.
  allowGroupDiscount?: boolean;
  // item 943 (QB AR-export epic) — when supplied (INVOICES only), each line gets a
  // "PERFORMED BY" employee dropdown (the old system's per-line Group Advisor). Required
  // on every line before the invoice can be finalized (server-gated).
  performedByUsers?: { id: string; name: string }[];
  // item 1336 — per-line T&M date column (WO/SO + invoice opt in; sits before PERFORMED BY)
  showLineDate?: boolean;
  // item 1452 (Chad: "mark that we're going to pull this sale from stock… often we order it at the time
  // of receiving it, those would just be a pass-through") — INVOICES only: a FROM STOCK toggle on each
  // HARDWARE line (services show a dash). Finalize pulls on-hand for the lines left ON; a line already
  // pulled reads "Pulled" and locks.
  showPullFromStock?: boolean;
  availableOn?: number; // item 39 — DocumentAvailability bit for the catalog picker filter
  makeItemFromCatalog: (catalogItem: Item, category: string) => TItem;
  toCatalogItem: (item: TItem) => Item;
}

const numberOr = (v: string): number => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};

// decimal(9,2) backend columns — clamp every computed money write so it can't 500 the save
const clampMoney = (n: number): number => Math.min(Math.round(n * 100) / 100, 9999999.99);

// item 556 — number cells no longer pre-fill a sticky "0" (Chad: typing over it gave "044").
// While focused the cell holds a per-focus draft string, so you type freely into a BLANK
// cell; empty/0 shows the placeholder instead of a literal 0 the caret has to fight. Money
// cells commit per keystroke (live totals); commitOnBlur cells (MSRP) push once on blur so
// derived displays don't flash intermediate nonsense while you type. Blur re-normalizes the
// display from the stored number ("044" → 44).
interface NumberCellProps {
  value: number | null | undefined;
  onCommit: (n: number) => void;
  blankZero?: boolean;
  commitOnBlur?: boolean;
  step?: string;
  placeholder?: string;
  title?: string;
}
const NumberCell: React.FC<NumberCellProps> = ({
  value, onCommit, blankZero, commitOnBlur, step, placeholder, title,
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const settled = value == null || (blankZero && value === 0) ? '' : String(value);
  return (
    <Input
      size="sm"
      variant="filled"
      type="number"
      step={step}
      min={0}
      textAlign="right"
      placeholder={placeholder}
      title={title}
      value={draft ?? settled}
      onFocus={(e) => { setDraft(settled); e.currentTarget.select(); }}
      onChange={(e) => { setDraft(e.target.value); if (!commitOnBlur) onCommit(numberOr(e.target.value)); }}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
      onBlur={() => { if (commitOnBlur && draft !== null) onCommit(numberOr(draft)); setDraft(null); }}
    />
  );
};
NumberCell.defaultProps = {
  blankZero: false, commitOnBlur: false, step: '0.01', placeholder: undefined, title: undefined,
};

// item 556 — the MSRP multiplier works BOTH ways and is never blank: it shows the exact
// derived ratio (MSRP ÷ group price, 4dp — item 434, typed MSRP is truth), else a multiplier
// you typed that couldn't apply yet (no group price on the line), else the company default
// markup as the starting point. Blur/Enter applies MSRP = group price × multiplier; a
// multiplier typed BEFORE the group price exists is kept and applied as soon as one does.
// Only a value the user actually edited commits — tabbing through never rewrites MSRP.
interface MultiplierCellProps {
  derived: string; // '' when underivable
  pending: number | undefined;
  fallback: number;
  onApply: (m: number) => void;
}
const MultiplierCell: React.FC<MultiplierCellProps> = ({
  derived, pending, fallback, onApply,
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const editedRef = useRef(false);
  const settled = derived || (pending && pending > 0 ? String(pending) : '') || String(fallback);
  return (
    <Input
      size="sm"
      variant="filled"
      type="number"
      step="any"
      min={0}
      textAlign="right"
      title="SRP MULTIPLIER of the group price — e.g. 1.3 = +30%. This is a multiplier, NOT a percent. SRP = group price × this."
      value={draft ?? settled}
      onFocus={(e) => { setDraft(settled); editedRef.current = false; e.currentTarget.select(); }}
      onChange={(e) => { setDraft(e.target.value); editedRef.current = true; }}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
      onBlur={() => {
        if (editedRef.current && draft !== null) {
          const m = numberOr(draft);
          if (m > 0) onApply(m);
        }
        setDraft(null);
      }}
    />
  );
};

const DocumentItemsFormElement = <TItem extends DocumentLineItemLike>(
  props: DocumentItemsFormElementProps<TItem>,
): React.ReactElement => {
  const {
    categoryNames, items, categoryButtonHandler, setItems, activeCategory, categoryEditMode,
    editCategoryName, setEditCategoryName,
    showCost, showRetail, showMsrpPair, showGrpPair, defaultGrpMult, showPriceTier,
    costAlwaysEditable, costLabel, totalLabel, vendorsOnly, performedByUsers, showLineDate, showPullFromStock, availableOn,
    defaultMsrpMult, makeItemFromCatalog, toCatalogItem, allowGroupDiscount,
  } = props;
  // item 1234 — the pending "% off group pricing" (WO/SO), applied on click
  const [groupDiscountPct, setGroupDiscountPct] = useState('');
  // item 556 — multipliers typed before the line has a group price, kept per line id
  // until a group price arrives to apply them against.
  const [pendingMult, setPendingMult] = useState<Record<string, number>>({});
  // canonical model — the same parking for the GRP markup (typed before a cost exists)
  const [pendingGrpMult, setPendingGrpMult] = useState<Record<string, number>>({});
  const { setItem: saveCatalogItem } = useItem({ id: NIL });
  // item 290 — catalog lookup so a reopened PO line (which doesn't persist its tier
  // snapshot) can still recover its dealer/distributor prices by itemId. Only consulted
  // when showPriceTier; react-query caches the list (the catalog combobox loads it too).
  const { data: catalog } = useItems();
  const catalogById = useMemo(() => {
    const m = new Map<string, Item>();
    if (showPriceTier) (catalog?.index ?? []).forEach((it) => m.set(it.id, it));
    return m;
  }, [showPriceTier, catalog]);
  const [openNotes, setOpenNotes] = useState<Record<string, boolean>>({});
  const getCategories = useMemo((): string[] => categoryNames.split(','), [categoryNames]);
  const labelColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const rowBorder = useColorModeValue('gray.100', 'whiteAlpha.100');

  // canonical model (2026-07-14) — item 434 removed the cost→group markup column; it's
  // BACK for hardware as the two-way GRP ×MULT pair (base cost/DISTRO → GROUP PRICE), so a
  // line reads the same as the catalog item. MSRP MARKUP (group price → suggested retail,
  // item 346) is the second pair, one tier up. Both markups are derived (never stored).
  const showPair = !!showMsrpPair;
  const showGrp = !!showGrpPair;
  // sku | brand | [tier] | [cost] [grp-markup] [retail] | [msrp-markup msrp] | qty | actions
  const tierCol = showPriceTier ? '7rem ' : '';
  const costCols = `${showCost ? '5.5rem ' : ''}${showGrp ? '5rem ' : ''}${showRetail ? '5.5rem ' : ''}${showPair ? '5rem 5.5rem ' : ''}`;

  // item 434 — EXACT ratios (Chad: "if I fill in MSRP it should fill in the markup at
  // 2.1357, whatever it takes"): the typed value is the stored truth; the ratio shown
  // derives at 4 decimals, display-only. MSRP markup = list ÷ group price; GRP markup =
  // group price ÷ cost.
  const msrpMarkupOf = (line: TItem): string => {
    const retail = Number((line as any).retailEach) || 0;
    const list = Number((line as any).listPrice) || 0;
    if (retail <= 0 || list <= 0) return '';
    return String(parseFloat((list / retail).toFixed(4)));
  };
  const grpMarkupOf = (line: TItem): string => {
    const cost = Number((line as any).costEach) || 0;
    const retail = Number((line as any).retailEach) || 0;
    if (cost <= 0 || retail <= 0) return '';
    return String(parseFloat((retail / cost).toFixed(4)));
  };
  // item 568 — SKU/BRAND get REAL minimums (they collapsed to slivers on narrow panels,
  // wrapping the BRAND header and hiding the inputs); the panel scrolls horizontally
  // instead of crushing the columns.
  const performedByCol = performedByUsers ? 'minmax(8rem,0.9fr) ' : '';
  const lineDateCol = showLineDate ? '8rem ' : ''; // item 1336 (Chad: "make the date first")
  const pullCol = showPullFromStock ? '7.5rem ' : ''; // item 1484b — widened for the Backordered box // item 1452 — FROM STOCK sits right before QTY
  const templateColumns = `${lineDateCol}minmax(9rem,1.2fr) minmax(6rem,1.2fr) ${tierCol}${costCols}${performedByCol}${pullCol}4.5rem 2rem`;

  const toast = useToast(); // item 951 — every add is ACKNOWLEDGED

  const updateItem = (id: string, patch: Partial<TItem>) => {
    setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };
  const removeItem = (id: string) => setItems(items.filter((it) => it.id !== id));
  const addCatalog = (catalogItem: Item, category: string) => {
    setItems([...items, makeItemFromCatalog(catalogItem, category)]);
    // item 951 (Chad) — adds were SILENT (a typed item was added 15 times with no
    // acknowledgment): every add now confirms what landed where.
    toast({ status: 'success', title: `Added ${catalogItem.sku || 'item'} to ${category}.`, duration: 2500 });
  };
  // item 531 — swap an EXISTING line to a different catalog SKU from inside its SKU cell
  // (Chad: "why can't I search right inside the SKU"). Rebuild the line's catalog-sourced
  // fields (sku/brand/cost/group price/MSRP/itemId/hardware/tax) from the picked item, but
  // keep the line's identity, its category, and the quantity/notes the user already set.
  const swapCatalog = (line: TItem, catalogItem: Item) => {
    const fresh = makeItemFromCatalog(catalogItem, line.category);
    updateItem(line.id, {
      ...fresh,
      id: line.id,
      quantity: (line as any).quantity,
      notes: line.notes,
    } as unknown as Partial<TItem>);
  };
  const addBlank = (typed: string, category: string) => {
    // item 951 — guard the silent-repeat: a still-blank line with this exact typed SKU
    // already exists in the category → don't add another copy, point at the existing one.
    const dupe = items.find((it) => it.category === category
      && (it as any).sku === typed
      && !(Number((it as any).retailEach) > 0));
    if (dupe) {
      toast({
        status: 'warning',
        title: `"${typed}" is already in ${category}`,
        description: 'Edit the existing line — it was not added again.',
        duration: 4000,
      });
      return;
    }
    setItems([...items, makeItemFromCatalog({ ...getEmptyItem(), sku: typed }, category)]);
    toast({ status: 'success', title: `Added ${typed || 'a new line'} to ${category}.`, duration: 2500 });
  };
  const duplicateRow = (item: TItem) => setItems([...items, { ...item, id: v4() } as TItem]);
  // create a reusable catalog Item from this line so it shows in the combobox next time (req 3)
  const saveToCatalog = (item: TItem) => saveCatalogItem({ ...toCatalogItem(item), id: NIL });

  // item 951 (Chad: "we are not marking up the skus on the invoice automatically") —
  // lines that arrive without an SRP (e.g. copied from the WO) auto-adopt
  // group price x the company default multiplier the moment they render. Idempotent:
  // only lines still missing a listPrice are stamped; a user-applied multiplier wins.
  React.useEffect(() => {
    if (!showPair) return;
    const mult = defaultMsrpMult ?? DEFAULT_PRICING_SETTINGS.srpMarkup;
    if (!mult || mult <= 1) return;
    const needs = items.filter((it) => Number((it as any).retailEach) > 0
      && !(Number((it as any).listPrice) > 0));
    if (needs.length === 0) return;
    setItems(items.map((it) => (needs.includes(it)
      ? { ...it, listPrice: clampMoney((Number((it as any).retailEach) || 0) * mult) } as TItem
      : it)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, showPair, defaultMsrpMult]);

  const clearPendingMult = (id: string) => setPendingMult((p) => {
    if (!(id in p)) return p;
    const { [id]: gone, ...rest } = p;
    return rest;
  });
  const clearPendingGrpMult = (id: string) => setPendingGrpMult((p) => {
    if (!(id in p)) return p;
    const { [id]: gone, ...rest } = p;
    return rest;
  });

  // item 556 — a money-cell commit. Editing the GROUP PRICE still drags MSRP/SRP along
  // (item 429: a suggestion below the price is nonsense) at the pending multiplier the
  // user typed if there is one, else the current derived ratio (only when > 1 — a ratio
  // of exactly 1 is the item-429 import artifact, never an intentional price call).
  // canonical model — when GROUP PRICE moves, MSRP (the suggestion above it) tracks it: at
  // the pending MSRP multiplier if the user typed one, else the current derived ratio (only
  // when > 1 — a ratio of exactly 1 is the item-429 import artifact). Returns the { listPrice }
  // fragment to fold into the SAME updateItem (one write — avoids setItems races).
  // item 995 (Chad: "where are the random multipliers coming from") — a DOLLAR AMOUNT typed
  // into the ×MULT cell (e.g. 10000 on a $5,500 line) exploded the SRP to the money-column
  // ceiling, and the cell then redisplayed the garbage ratio (9,999,999.99 ÷ 5,500 = 1818.18).
  // No legitimate markup is anywhere near this; refuse to apply or cascade one.
  const MAX_SANE_MULT = 50;

  const msrpCascade = (item: TItem, newRetail: number): Record<string, number> => {
    if (!showMsrpPair || newRetail <= 0) return {};
    const pm = pendingMult[item.id];
    const derived = Number(msrpMarkupOf(item));
    if (!((pm && pm > 0) || derived > 1)) return {};
    const ratio = pm && pm > 0 ? pm : derived;
    if (ratio > MAX_SANE_MULT) return {}; // item 995 — never drag SRP with a garbage ratio
    return { listPrice: clampMoney(newRetail * ratio) };
  };

  const commitMoney = (item: TItem, key: 'costEach' | 'retailEach', v: number) => {
    // canonical model — editing DISTRO drags GROUP PRICE (hardware GRP pair), which in turn
    // drags MSRP; all in one write.
    if (key === 'costEach' && showGrp && (item as any).isHardware) {
      const pm = pendingGrpMult[item.id];
      const derived = Number(grpMarkupOf(item));
      if (v > 0 && pm && pm > 0) clearPendingGrpMult(item.id);
      if (v > 0 && ((pm && pm > 0) || derived > 1)) {
        const ratio = pm && pm > 0 ? pm : derived;
        const newRetail = clampMoney(v * ratio);
        updateItem(item.id, {
          costEach: v, retailEach: newRetail, ...msrpCascade(item, newRetail),
        } as unknown as Partial<TItem>);
        return;
      }
    }
    // item 556 — editing GROUP PRICE drags MSRP.
    if (key === 'retailEach' && showMsrpPair) {
      const pm = pendingMult[item.id];
      const derivedRatio = Number(msrpMarkupOf(item));
      if (v > 0 && pm && pm > 0) clearPendingMult(item.id);
      if (v > 0 && ((pm && pm > 0) || derivedRatio > 1)) {
        const ratio = pm && pm > 0 ? pm : derivedRatio;
        // item 995 — a garbage ratio (a price once typed into ×MULT) must not keep cascading
        if (ratio <= MAX_SANE_MULT) {
          updateItem(item.id, { retailEach: v, listPrice: clampMoney(v * ratio) } as unknown as Partial<TItem>);
          return;
        }
      }
    }
    updateItem(item.id, { [key]: v } as unknown as Partial<TItem>);
  };

  // item 1244/1290 — the live discount percentage off the STANDARD group price, derived from
  // whatever price the line currently holds (so an edited price re-states its own math).
  const discountPctOf = (line: TItem): number => {
    const std = Number((line as any).preDiscountRetailEach) || 0;
    const retail = Number((line as any).retailEach) || 0;
    if (std <= 0) return 0;
    return Math.round((1 - retail / std) * 10000) / 100;
  };
  // The GROUP PRICE cell. item 1244 (Chad/Tom's scare: "no realization it happened") — a
  // DISCOUNTED line keeps the STANDARD group price visible and shows the math right in the
  // cell; on a doc that OWNS the discount the price locks until Clear discount.
  // item 1290 — a BILLED doc (invoice) inherits the discount but has no Clear button, so
  // locking there would trap the editor: it shows the same math as a caption over an EDITABLE
  // cell (the % re-derives from whatever price is typed, so the read stays honest either way).
  const priceCell = (item: TItem) => (
    <NumberCell
      blankZero
      placeholder="0.00"
      value={(item as any).retailEach}
      onCommit={(v) => commitMoney(item, 'retailEach', v)}
    />
  );
  const retailCell = (item: TItem) => {
    const std = Number((item as any).preDiscountRetailEach) || 0;
    if (std <= 0 || std === Number((item as any).retailEach)) return priceCell(item);
    if (allowGroupDiscount) {
      return (
        <Box textAlign="right" pr={1} title="Discount applied — Clear discount (below the items) to edit prices">
          <Text fontSize="xs" color={labelColor}>{`std ${money(std)}`}</Text>
          <Text fontSize="sm" fontWeight="semibold" color="orange.300">
            {`−${discountPctOf(item)}% = ${money(Number((item as any).retailEach) || 0)}`}
          </Text>
        </Box>
      );
    }
    return (
      <Box pr={1} title="Discounted on the sales order this was billed from">
        <Text fontSize="xs" textAlign="right" color="orange.300">
          {`std ${money(std)} −${discountPctOf(item)}%`}
        </Text>
        {priceCell(item)}
      </Box>
    );
  };

  // item 556 — a multiplier commit: with a group price on the line it sets MSRP right
  // away; without one it's remembered (pendingMult) and applied when the price arrives.
  const applyMultiplier = (item: TItem, m: number) => {
    // item 995 — refuse a price typed into the multiplier cell
    if (m > MAX_SANE_MULT) {
      toast({
        status: 'warning',
        title: `${m} doesn't look like a multiplier`,
        description: '×MULT multiplies the group price — e.g. 1.3 = +30%. Type the dollar amount into the SRP/group price cells instead.',
        duration: 6000,
      });
      return;
    }
    const retail = Number((item as any).retailEach) || 0;
    if (retail > 0) {
      updateItem(item.id, { listPrice: clampMoney(retail * m) } as unknown as Partial<TItem>);
      clearPendingMult(item.id);
    } else {
      setPendingMult((p) => ({ ...p, [item.id]: m }));
    }
  };

  // canonical model — GRP ×MULT applied: with a cost on the line, GROUP PRICE = cost × m
  // (and MSRP cascades off the new group price); else park it until a cost arrives.
  const applyGrpMultiplier = (item: TItem, m: number) => {
    // item 995 — refuse a price typed into the multiplier cell
    if (m > MAX_SANE_MULT) {
      toast({
        status: 'warning',
        title: `${m} doesn't look like a multiplier`,
        description: 'GRP ×MULT multiplies the cost — e.g. 2 = double. Type the dollar amount into the price cells instead.',
        duration: 6000,
      });
      return;
    }
    const cost = Number((item as any).costEach) || 0;
    if (cost > 0) {
      const newRetail = clampMoney(cost * m);
      updateItem(item.id, { retailEach: newRetail, ...msrpCascade(item, newRetail) } as unknown as Partial<TItem>);
      clearPendingGrpMult(item.id);
    } else {
      setPendingGrpMult((p) => ({ ...p, [item.id]: m }));
    }
  };

  // item 290 — the dealer/distributor tiers for a PO line: the snapshot taken at add-time
  // wins; for a reopened PO (no snapshot persisted) fall back to the catalog by itemId.
  const lineTiers = (line: TItem): PoTierCosts => {
    const l = line as any;
    if (l.dealerCostEach != null || l.distributorCostEach != null) {
      return { dealer: l.dealerCostEach, distributor: l.distributorCostEach ?? undefined };
    }
    const cat = l.itemId ? catalogById.get(l.itemId) : undefined;
    return cat ? poTierCosts(cat) : { dealer: l.costEach, distributor: undefined };
  };

  // switch a PO line to a tier: record the choice and refill costEach with that tier's price
  const changeTier = (line: TItem, tier: PoPriceTier) => {
    const tiers = lineTiers(line);
    const cost = poTierCost(tier, tiers);
    updateItem(line.id, {
      priceTier: tier,
      ...(cost != null ? { costEach: cost } : {}),
    } as unknown as Partial<TItem>);
  };

  return (
    <>
      {/* item 1336 (Chad: "the items section ideally would always be expanded by all
          categories with the reverse option to collapse" — ALL doc types) — every category
          starts OPEN; the chevron still collapses. Keyed by the category list so a freshly
          added category also opens (a structural change remounts; a collapse click within
          the same structure is preserved). */}
      <Accordion
        key={getCategories.join('|')}
        defaultIndex={getCategories.map((_, i) => i)}
        allowMultiple
        w="100%"
      >
        {getCategories.map((categoryName) => {
          const categoryItems = items.filter(
            (i) => (i.category || '').toUpperCase() === categoryName.toUpperCase(),
          );
          const editingThis = categoryEditMode && activeCategory.toUpperCase() === categoryName.toUpperCase();
          return (
            <AccordionItem key={categoryName} border="none" mb={2}>
              <HStack>
                <Menu isLazy>
                  <MenuButton type="button" fontSize="sm" _hover={{ color: 'brandGreen.400' }}>
                    <FiMoreVertical />
                  </MenuButton>
                  <Portal>
                    <MenuList fontSize="sm">
                      <MenuItem icon={<ArrowUpIcon />} onClick={(e) => categoryButtonHandler('MOVEUP', categoryName, e)}>MOVE UP</MenuItem>
                      <MenuItem icon={<ArrowDownIcon />} onClick={(e) => categoryButtonHandler('MOVEDN', categoryName, e)}>MOVE DOWN</MenuItem>
                      <MenuItem icon={<FiCopy />} onClick={(e) => categoryButtonHandler('DUPLICATE', categoryName, e)}>DUPLICATE</MenuItem>
                      <MenuItem icon={<FaEdit />} onClick={(e) => categoryButtonHandler('EDIT', categoryName, e)}>RENAME</MenuItem>
                      <MenuItem icon={<FaTrash />} onClick={(e) => categoryButtonHandler('REMOVE', categoryName, e)}>REMOVE</MenuItem>
                    </MenuList>
                  </Portal>
                </Menu>
                <AccordionButton p={2} sx={{ '&:focus': { boxShadow: 'none' }, '&:focus-visible': { boxShadow: 'outline' } }} borderRadius="md">
                  <Flex flexBasis="80%" align="center">
                    {editingThis ? (
                      <Input
                        type="text"
                        size="sm"
                        fontSize="sm"
                        w={{ base: 'full', md: '20rem' }}
                        value={editCategoryName}
                        autoFocus
                        onKeyPress={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        // item 555 — commit the rename on click-away too (not just Enter). The
                        // name was silently lost when you typed it and moved on / opened the PDF.
                        onBlur={(e) => categoryButtonHandler('EDITCOMPLETE', editCategoryName, e)}
                        onChange={(e) => setEditCategoryName(e.target.value)}
                        onClick={(e) => e.preventDefault()}
                      />
                    ) : (
                      <Text fontSize="sm" fontWeight="semibold">{categoryName}</Text>
                    )}
                    <Text fontSize="xs" color={labelColor} ml={2}>{`(${categoryItems.length})`}</Text>
                  </Flex>
                  <Box flex={1} />
                  <AccordionIcon />
                </AccordionButton>
              </HStack>

              <AccordionPanel px={0} pt={2} pb={3} overflowX="auto">
                {/* column headers */}
                {categoryItems.length > 0 && (
                <Grid templateColumns={templateColumns} gap={2} px={1} mb={1}>
                  {showLineDate && (
                    <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>DATE</Text>
                  )}
                  <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>SKU</Text>
                  <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>BRAND</Text>
                  {showPriceTier && (
                    <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>TIER</Text>
                  )}
                  {showCost && (
                    <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>{costLabel ?? 'COST'}</Text>
                  )}
                  {showGrp && (
                    <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>GRP ×MULT</Text>
                  )}
                  {showRetail && (
                    <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>GROUP PRICE</Text>
                  )}
                  {showPair && (
                    <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>SRP ×MULT</Text>
                  )}
                  {showPair && (
                    <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>SRP</Text>
                  )}
                  {performedByUsers && (
                    <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>PERFORMED BY</Text>
                  )}
                  {showPullFromStock && (
                    <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" color={labelColor} title="On = pull this quantity from on-hand when the invoice is finalized. Off = pass-through (ordered for this job); on-hand untouched. Backordered = bills 0 now, prints a red BACKORDERED flag, and the order stays In Progress.">FROM STOCK</Text>
                  )}
                  <Text fontSize="0.6rem" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>QTY</Text>
                  <Box />
                </Grid>
                )}

                {/* inline-editable rows */}
                {categoryItems.map((item) => (
                  <Box key={item.id} borderBottomWidth="1px" borderBottomColor={rowBorder} py={1}>
                    <Grid
                      templateColumns={templateColumns}
                      gap={2}
                      px={1}
                      alignItems="center"
                    >
                      {/* item 1336 (Chad) — the T&M DATE leads the row */}
                      {showLineDate && (
                        <Input
                          size="sm"
                          type="date"
                          value={((item as any).lineDate ?? '').slice(0, 10)}
                          onChange={(e) => updateItem(item.id, { lineDate: e.target.value || null } as unknown as Partial<TItem>)}
                        />
                      )}
                      {/* item 531 — SKU cell is a live catalog search: pick to swap the
                        line to another SKU, or free-type to set an ad-hoc SKU. */}
                      <CatalogCombobox
                        inline
                        vendorsOnly={vendorsOnly}
                        availableOn={availableOn}
                        value={item.sku ?? ''}
                        onTextChange={(text) => updateItem(item.id, { sku: text } as Partial<TItem>)}
                        onPick={(catalogItem) => swapCatalog(item, catalogItem)}
                        onCreateNew={() => undefined}
                      />
                      <Input size="sm" variant="filled" value={item.brand ?? ''} onChange={(e) => updateItem(item.id, { brand: e.target.value } as Partial<TItem>)} />
                      {showPriceTier && (() => {
                        const tiers = lineTiers(item);
                        // no distributor tier on this item → dealer is the only price; show it
                        // read-only so the row stays aligned and the intent is explicit.
                        if (tiers.distributor == null) {
                          return <Text fontSize="xs" color={labelColor} pl={1}>Dealer</Text>;
                        }
                        const current = resolvePoTier((item as any).priceTier, (item as any).costEach, tiers);
                        return (
                          <Select
                            size="sm"
                            variant="filled"
                            value={current}
                            onChange={(e) => changeTier(item, e.target.value as PoPriceTier)}
                          >
                            <option value="dealer">Dealer</option>
                            <option value="distributor">Distributor</option>
                          </Select>
                        );
                      })()}
                      {/* DISTRO (cost) — item 425: HARDWARE concept; service rows show an
                        aligned dash (editor-only, never on a customer PDF). item 554 EXCEPT
                        on expense docs (costAlwaysEditable): a PO/Bill line is real money you
                        pay, so labor/custom lines keep an editable DISTO cost. */}
                      {showCost && (
                        !(item as any).isHardware && !costAlwaysEditable ? (
                          <Text fontSize="xs" color={labelColor} textAlign="right" pr={2}>—</Text>
                        ) : (
                          <NumberCell
                            blankZero
                            placeholder="0.00"
                            value={(item as any).costEach}
                            onCommit={(v) => commitMoney(item, 'costEach', v)}
                          />
                        )
                      )}
                      {/* canonical model — GRP ×MULT ⇄ GROUP PRICE: hardware only (needs a cost
                        base); service rows show a dash. Either side derives the other. */}
                      {showGrp && (
                        (item as any).isHardware ? (
                          <MultiplierCell
                            derived={grpMarkupOf(item)}
                            pending={pendingGrpMult[item.id]}
                            fallback={defaultGrpMult ?? DEFAULT_PRICING_SETTINGS.groupMarkup}
                            onApply={(m) => applyGrpMultiplier(item, m)}
                          />
                        ) : (
                          <Text fontSize="xs" color={labelColor} textAlign="right" pr={2}>—</Text>
                        )
                      )}
                      {showRetail && retailCell(item)}
                      {/* item 346/434/556 — was a two-way pair; item 925 (Chad): the
                        MULTIPLIER is the ONLY input now (typing 1.3 into the value cell
                        stored $1.30 as the retail price → a $2.60 'suggested retail
                        total' on a $3,000 job). The SRP cell is computed display. */}
                      {showPair && (
                      <MultiplierCell
                        derived={msrpMarkupOf(item)}
                        pending={pendingMult[item.id]}
                        fallback={defaultMsrpMult ?? DEFAULT_PRICING_SETTINGS.srpMarkup}
                        onApply={(m) => applyMultiplier(item, m)}
                      />
                      )}
                      {showPair && (
                      <Input
                        size="sm"
                        variant="filled"
                        isReadOnly
                        tabIndex={-1}
                        textAlign="right"
                        opacity={0.75}
                        title="SRP (suggested retail) — computed: group price × the SRP multiplier"
                        value={(() => {
                          const lp = Number((item as any).listPrice) || 0;
                          return lp > 0 ? lp.toFixed(2) : '';
                        })()}
                      />
                      )}
                      {/* item 943 — per-line "Performed by": who did the work (the QB export
                        suffixes service SKUs with their QB employee ID). Finalize is blocked
                        server-side until every line has one. */}
                      {performedByUsers && (
                        <Select
                          size="sm"
                          variant="filled"
                          placeholder="Who?"
                          borderColor={item.employeeId ? undefined : 'orange.400'}
                          borderWidth={item.employeeId ? undefined : '1px'}
                          value={item.employeeId ?? ''}
                          onChange={(e) => updateItem(item.id, { employeeId: e.target.value || null } as Partial<TItem>)}
                        >
                          {performedByUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </Select>
                      )}
                      {/* item 1452 — FROM STOCK: hardware lines toggle; services never touch inventory */}
                      {showPullFromStock && (() => {
                        const li = item as any;
                        if (!li.isHardware) return <Text fontSize="xs" color={labelColor} textAlign="center">—</Text>;
                        if (li.stockPulledDate) {
                          return <Text fontSize="xs" color="green.400" fontWeight="semibold" textAlign="center" title={`Pulled from on-hand ${String(li.stockPulledDate).slice(0, 10)}`}>Pulled</Text>;
                        }
                        // item 1484b (Chad/Tom) — BACKORDERED: the line ships later. Checking it zeroes the
                        // quantity (bills $0, keeps the order In Progress — item 1484) and remembers how many
                        // follow; the PDF prints a red "BACKORDERED · N to follow". Unchecking restores the qty.
                        // item 1577 — PARTIAL: the "to follow" number can be lowered (4 ordered, 1 to
                        // follow → the line bills 3 now). Stock/Pass stays available for what ships now.
                        const bo = Number(li.backorderedQty) > 0;
                        const shipsNow = Number(li.quantity) > 0;
                        return (
                          <VStack spacing={0.5} align="center">
                            {(!bo || shipsNow) && (
                              <HStack spacing={1} justify="center" title={li.pullFromStock ? 'Pull from on-hand at finalize' : 'Pass-through — ordered for this job; on-hand untouched'}>
                                <Switch
                                  size="sm"
                                  colorScheme="green"
                                  isChecked={!!li.pullFromStock}
                                  onChange={(e) => updateItem(item.id, { pullFromStock: e.target.checked } as unknown as Partial<TItem>)}
                                />
                                <Text fontSize="xs" color={labelColor}>{li.pullFromStock ? 'Stock' : 'Pass'}</Text>
                              </HStack>
                            )}
                            <Checkbox
                              size="sm"
                              colorScheme="red"
                              isChecked={bo}
                              title={bo ? `${li.backorderedQty} to follow on a later invoice — uncheck to bill it now` : 'Backordered — bill 0 now, the rest follows on a later invoice'}
                              onChange={(e) => updateItem(item.id, (e.target.checked
                                ? { ...startBackorder(Number(li.quantity)), pullFromStock: false }
                                : clearBackorder(Number(li.quantity), Number(li.backorderedQty))) as unknown as Partial<TItem>)}
                            >
                              <Text fontSize="xs" color={bo ? 'red.400' : labelColor} fontWeight={bo ? 'bold' : 'normal'}>
                                {bo ? 'B/O' : 'Backordered'}
                              </Text>
                            </Checkbox>
                            {bo && (
                              <HStack spacing={1} justify="center" title="How many follow on a later invoice — the rest of the line bills now">
                                <Input
                                  size="xs"
                                  w="3.2rem"
                                  type="number"
                                  min={1}
                                  step="any"
                                  textAlign="center"
                                  aria-label="Units to follow"
                                  value={Number(li.backorderedQty)}
                                  onChange={(e) => updateItem(item.id, setToFollow(
                                    Number(li.quantity), Number(li.backorderedQty), Number(e.target.value),
                                  ) as unknown as Partial<TItem>)}
                                />
                                <Text fontSize="0.6rem" color="red.400">to follow</Text>
                              </HStack>
                            )}
                          </VStack>
                        );
                      })()}
                      <NumberCell
                        step="any"
                        value={(item as any).quantity ?? 1}
                        onCommit={(v) => updateItem(item.id, { quantity: v } as Partial<TItem>)}
                      />
                      <Menu isLazy>
                        <MenuButton as={IconButton} aria-label="row actions" icon={<FiMoreVertical />} size="xs" variant="ghost" />
                        <Portal>
                          <MenuList fontSize="sm">
                            <MenuItem icon={<FiFileText />} onClick={() => setOpenNotes((o) => ({ ...o, [item.id]: !o[item.id] }))}>
                              {item.notes || openNotes[item.id] ? 'HIDE NOTE' : 'ADD NOTE'}
                            </MenuItem>
                            <MenuItem icon={<FiCopy />} onClick={() => duplicateRow(item)}>DUPLICATE</MenuItem>
                            <MenuItem icon={<FiBookmark />} onClick={() => saveToCatalog(item)}>SAVE TO CATALOG</MenuItem>
                            <MenuItem icon={<FaTrash />} onClick={() => removeItem(item.id)}>REMOVE</MenuItem>
                          </MenuList>
                        </Portal>
                      </Menu>
                    </Grid>
                    {/* expandable per-line note (item 80) */}
                    {(openNotes[item.id] || item.notes) && (
                    <Box px={1} pt={1}>
                      <Textarea
                        size="sm"
                        rows={2}
                        variant="filled"
                        placeholder="Note for this line…"
                        value={item.notes ?? ''}
                        onChange={(e) => updateItem(item.id, { notes: e.target.value } as Partial<TItem>)}
                      />
                    </Box>
                    )}
                  </Box>
                ))}

                {/* add bar — search the catalog or type to add new */}
                <Box mt={3} px={1} maxW="36rem">
                  <CatalogCombobox
                    vendorsOnly={vendorsOnly}
                    availableOn={availableOn}
                    onPick={(catalogItem) => addCatalog(catalogItem, categoryName)}
                    onCreateNew={(typed) => addBlank(typed, categoryName)}
                  />
                </Box>
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
      {/* item 459 — LIVE totals for the whole document, right on the form (Chad:
          "you have to see the pdf to see the totals"). Recomputes from the current
          line state on every edit; money() keeps it stealth-aware. */}
      {/* item 1234 (Chad) — WO/SO: one-shot % discount off GROUP pricing, every line
          (services AND hardware). Rewrites each line's retailEach — the binding group
          price — and nothing else (cost + MSRP untouched). Repeat applications compound
          (each click discounts the CURRENT group prices); fix a mistake by re-editing
          lines or leaving without saving. */}
      {allowGroupDiscount && items.length > 0 && (
        <Flex justify="flex-end" align="center" px={2} pt={3} sx={{ gap: '8px' }} wrap="wrap">
          {items.some((it) => Number((it as any).preDiscountRetailEach) > 0) && (
            <Text fontSize="xs" fontWeight="bold" color="orange.300">
              ⚠ DISCOUNT ACTIVE — lines show standard − discount; Clear to edit prices
            </Text>
          )}
          <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>
            DISCOUNT % OFF GROUP PRICING
          </Text>
          <Input
            size="sm"
            maxW="90px"
            type="number"
            placeholder="e.g. 10"
            value={groupDiscountPct}
            onChange={(e) => setGroupDiscountPct(e.target.value)}
          />
          <Box
            as="button"
            type="button"
            px={3}
            py={1}
            fontSize="sm"
            fontWeight="semibold"
            borderWidth="1px"
            borderRadius="md"
            onClick={() => {
              const pct = parseFloat(groupDiscountPct);
              if (!Number.isFinite(pct) || pct <= 0 || pct >= 100) {
                toast({ status: 'warning', title: 'Enter a discount between 0 and 100.' });
                return;
              }
              const factor = 1 - pct / 100;
              // item 1239 — the ORIGINAL group price survives on each line so the PDF can
              // show the math (X − p% = Y); re-applying REPLACES the discount (recomputed
              // from the originals — never compounds).
              setItems(items.map((it) => {
                const base = Number((it as any).preDiscountRetailEach) || Number((it as any).retailEach) || 0;
                return {
                  ...it,
                  preDiscountRetailEach: base,
                  retailEach: Math.round(base * factor * 100) / 100,
                };
              }));
              setGroupDiscountPct('');
              toast({ status: 'success', title: `${pct}% off group pricing applied to ${items.length} line${items.length === 1 ? '' : 's'}.`, description: 'Re-applying replaces (never stacks). Save the document to keep it.' });
            }}
          >
            Apply
          </Box>
          {items.some((it) => Number((it as any).preDiscountRetailEach) > 0) && (
            <Box
              as="button"
              type="button"
              px={3}
              py={1}
              fontSize="sm"
              fontWeight="semibold"
              borderWidth="1px"
              borderColor="orange.300"
              color="orange.300"
              borderRadius="md"
              onClick={() => {
                // item 1239 — undo: restore every line to its stored original group price
                setItems(items.map((it) => {
                  const base = Number((it as any).preDiscountRetailEach);
                  return base > 0
                    ? { ...it, retailEach: base, preDiscountRetailEach: null }
                    : it;
                }));
                toast({ status: 'info', title: 'Discount cleared — lines back at standard group pricing.' });
              }}
            >
              Clear discount
            </Box>
          )}
        </Flex>
      )}
      {items.length > 0 && (showRetail || showCost) && (
        <HStack justify="flex-end" spacing={6} px={2} py={3} borderTopWidth="1px" borderTopColor={rowBorder}>
          {showCost && (
            <HStack spacing={2}>
              <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>{costLabel ?? 'COST'}</Text>
              <Text fontSize="sm" fontWeight="semibold">
                {money(items.reduce((s, it) => s + (Number((it as any).costEach) || 0) * (Number((it as any).quantity) || 0), 0))}
              </Text>
            </HStack>
          )}
          {showRetail && (
            <HStack spacing={2}>
              <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" color={labelColor}>{totalLabel ?? 'TOTAL'}</Text>
              <Text fontSize="sm" fontWeight="semibold">
                {money(items.reduce((s, it) => s
                  + ((Number((it as any).retailEach) || 0) + (Number((it as any).taxEach) || 0))
                  * (Number((it as any).quantity) || 0), 0))}
              </Text>
            </HStack>
          )}
        </HStack>
      )}
    </>
  );
};

DocumentItemsFormElement.defaultProps = {
  showCost: false,
  showRetail: false,
  defaultMsrpMult: undefined,
  showGrpPair: false,
  defaultGrpMult: undefined,
  showPriceTier: false,
  costAlwaysEditable: false,
  costLabel: undefined,
  totalLabel: undefined,
  vendorsOnly: false,
  performedByUsers: undefined,
  showLineDate: undefined,
  showPullFromStock: undefined,
  availableOn: undefined,
  allowGroupDiscount: false, // item 1234
};

export default DocumentItemsFormElement;
